import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Modal from '../components/Modal';
import RelatedLinks from '../components/RelatedLinks';
import { useModal } from '../components/useModal';

/**
 * PLAY event — a curated event and its sessions.
 *
 * Sessions are edited inline: each row is its own small form, because the fields
 * are few and the alternative is a page per session for four numbers.
 *
 * **Pairings stay in the old admin.** Placing players on courts there is a
 * drag-and-drop board that persists every move with its own PATCH, and
 * rebuilding that unattended is how a move ends up looking applied and not being
 * saved. The session link opens it; noted in REWRITE-STATE.md.
 *
 * Deleting a session is refused by the backend when it has (or ever had) signups,
 * a waitlist or pairings. The old page states that inside a `window.confirm`,
 * after you have decided; here the row knows its current counts, so Delete is
 * only offered on a session that has none of them.
 */

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 16) : '—');

// `datetime-local` has no timezone: whatever is typed is read as local time by
// `new Date(...)`. So the instant has to be *converted* to local components on the
// way in, not sliced out of the ISO string — slicing keeps the UTC wall clock and
// the conversion back then shifts the session by the offset on every save, which
// in summer is two hours. The old page gets this right and labels the fields
// "local time"; this keeps both.
const toLocalInput = value => {
  if (!value) return '';
  const date = new Date(value);
  const pad = number => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const toIso = value => (value ? new Date(value).toISOString() : null);

const buildBody = form => ({
  startsAtUtc: toIso(form.startsAtUtc),
  endsAtUtc: toIso(form.endsAtUtc),
  capacity: Number(form.capacity),
  courtCount: Number(form.courtCount),
  signupDeadlineUtc: toIso(form.signupDeadlineUtc)
});

const emptyForm = {
  startsAtUtc: '', endsAtUtc: '', capacity: '', courtCount: '', signupDeadlineUtc: ''
};

const PlayEventInternal = ({ api }) => {
  const { eventId } = useParams();
  const { ask, modalProps } = useModal();

  const [event, setEvent] = useState(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api.playEvents.get(eventId);
      if (!data || !data.id) {
        setFailed(true);
        return;
      }
      setEvent(data);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, eventId]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/play-events">← All events</BackToList>
        <div className="next-note next-note--error">
          Could not load this event.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!event) return <div className="next-page next-muted">Loading…</div>;

  const sessions = Array.isArray(event.sessions) ? event.sessions : [];

  const valid = form => form
    && form.startsAtUtc
    && form.endsAtUtc
    && Number(form.capacity) > 0
    && Number(form.courtCount) > 0;

  const run = async (fn, okText, failText) => {
    setBusy(true);
    try {
      await fn();
      setNote({ tone: 'info', text: okText });
      await load();
      return true;
    } catch (error) {
      setNote({ tone: 'error', text: `${failText}: ${error?.message || 'unknown error'}` });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveSession = async () => {
    if (!valid(editing)) return;
    const ok = await run(
      () => api.playEvents.updateSession(editing.id, buildBody(editing)),
      'Session saved.',
      'Unable to save the session'
    );
    if (ok) setEditing(null);
  };

  const addSession = async () => {
    if (!valid(adding)) return;
    const ok = await run(
      () => api.playEvents.addSession(eventId, buildBody(adding)),
      'Session added.',
      'Unable to add the session'
    );
    if (ok) setAdding(null);
  };

  // Offered only for a session with no signup, waitlist or active pairings (the
  // button is hidden otherwise). The backend is stricter still: any history
  // counts, cancelled signups and replaced pairings included, and its 409 is
  // turned into a readable reason by the API module.
  const deleteSession = session => {
    ask({
      kind: 'danger',
      title: 'Delete this session?',
      context: `Delete · ${fmtDate(session.startsAtUtc)}`,
      body: 'The session and its slot on the event go. Nobody is signed up, so nobody is affected.',
      warning: 'This cannot be undone from the admin. It is refused if the session ever had a signup, a waitlist entry or pairings, even cancelled or replaced ones.',
      confirmLabel: 'Delete session',
      onConfirm: () => run(
        () => api.playEvents.deleteSession(session.id),
        'Session deleted.',
        'Unable to delete the session'
      )
    });
  };

  const sessionForm = (form, setForm) => (
    <div className="next-form__grid">
      <label className="next-filter">
        <span className="next-filter__label">Starts (local time)</span>
        <input
          className="next-input"
          type="datetime-local"
          value={form.startsAtUtc}
          onChange={event2 => setForm({ ...form, startsAtUtc: event2.target.value })}
        />
      </label>
      <label className="next-filter">
        <span className="next-filter__label">Ends (local time)</span>
        <input
          className="next-input"
          type="datetime-local"
          value={form.endsAtUtc}
          onChange={event2 => setForm({ ...form, endsAtUtc: event2.target.value })}
        />
      </label>
      <label className="next-filter">
        <span className="next-filter__label">Capacity</span>
        <input
          className="next-input"
          type="number"
          value={form.capacity}
          onChange={event2 => setForm({ ...form, capacity: event2.target.value })}
        />
      </label>
      <label className="next-filter">
        <span className="next-filter__label">Courts</span>
        <input
          className="next-input"
          type="number"
          value={form.courtCount}
          onChange={event2 => setForm({ ...form, courtCount: event2.target.value })}
        />
      </label>
      <label className="next-filter">
        <span className="next-filter__label">Signup deadline (local time)</span>
        <input
          className="next-input"
          type="datetime-local"
          value={form.signupDeadlineUtc}
          onChange={event2 => setForm({ ...form, signupDeadlineUtc: event2.target.value })}
        />
        <span className="next-form__hint">Optional — the event’s own default applies when empty.</span>
      </label>
    </div>
  );

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/play-events">← All events</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{event.nameEn || event.slug || 'Event'}</h1>
          {event.status && <span className="next-pill next-pill--info">{event.status}</span>}
          {event.code && <span className="next-pill next-pill--neutral">{event.code}</span>}
        </div>
        <span className="next-detail__id">{event.id}</span>
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="next-detail">
        <div className="next-detail__main">
          <section className="next-card">
            <div className="next-card__head"><h2>Event</h2></div>
            <div className="next-facts">
              {[
                ['Sport', event.sport],
                ['Affiliation', event.affiliationName],
                ['Location', event.locationName],
                ['Address', event.locationAddress],
                ['Slug', event.slug],
                ['Code', event.code]
              ].map(([label, value]) => (
                <div className="next-fact" key={label}>
                  <span className="next-fact__label">{label}</span>
                  <span className="next-fact__value">{value || <span className="next-muted">—</span>}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head">
              <h2>Sessions</h2>
              <span className="next-muted">{sessions.length}</span>
              <span className="next-card__spacer" />
              <button
                type="button"
                className="next-btn next-btn--sm"
                disabled={busy || !!adding}
                onClick={() => setAdding({ ...emptyForm })}
              >
                Add a session
              </button>
            </div>

            {adding && (
              <div className="next-form__section">
                {sessionForm(adding, setAdding)}
                <div className="next-form__actions">
                  <button
                    type="button"
                    className="next-btn next-btn--primary"
                    disabled={!valid(adding) || busy}
                    title={valid(adding) ? undefined : 'Start, end, capacity and courts are required'}
                    onClick={addSession}
                  >
                    Add session
                  </button>
                  <button type="button" className="next-btn" onClick={() => setAdding(null)}>Cancel</button>
                </div>
              </div>
            )}

            <div className="next-table-wrap">
              <table className="next-table next-table--wide next-table--stack">
                <thead>
                  <tr>
                    <th>Starts</th>
                    <th>Ends</th>
                    <th className="next-table__right">Capacity</th>
                    <th className="next-table__right">Signed up</th>
                    <th className="next-table__right">Waitlist</th>
                    <th className="next-table__right">Courts</th>
                    <th>Pairings</th>
                    <th className="next-table__right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.length === 0 && (
                    <tr><td colSpan={8} className="next-muted next-table__note">No session on this event.</td></tr>
                  )}

                  {sessions.map(session => (
                    <tr key={session.id}>
                      <td data-label="" className="next-nowrap">{fmtDate(session.startsAtUtc)}</td>
                      <td data-label="Ends" className="next-nowrap">{fmtDate(session.endsAtUtc)}</td>
                      <td data-label="Capacity" className="next-table__right">{session.capacity ?? '—'}</td>
                      <td data-label="Signed up" className="next-table__right">{session.registeredCount ?? 0}</td>
                      <td data-label="Waitlist" className="next-table__right">{session.watchlistCount ?? 0}</td>
                      <td data-label="Courts" className="next-table__right">{session.courtCount ?? '—'}</td>
                      <td data-label="Pairings">
                        <span className={`next-pill next-pill--${session.hasActivePairings ? 'ok' : 'neutral'}`}>
                          {session.hasActivePairings ? 'Ready' : 'None'}
                        </span>
                      </td>
                      <td className="next-table__right" data-label="">
                        <div className="next-actions next-actions--end">
                          <Link
                            className="next-btn next-btn--sm"
                            to={`/next/play-event/${eventId}/session/${session.id}`}
                          >
                            Pairings
                          </Link>
                          <button
                            type="button"
                            className="next-btn next-btn--sm"
                            disabled={busy}
                            onClick={() => setEditing({
                              id: session.id,
                              startsAtUtc: toLocalInput(session.startsAtUtc),
                              endsAtUtc: toLocalInput(session.endsAtUtc),
                              capacity: session.capacity ?? '',
                              courtCount: session.courtCount ?? '',
                              signupDeadlineUtc: toLocalInput(session.effectiveSignupDeadlineUtc)
                            })}
                          >
                            Edit
                          </button>
                          {/* Only offered when the backend can allow it: a session with
                              signups, a waitlist or pairings is always refused. */}
                          {!((session.registeredCount || 0) > 0
                            || (session.watchlistCount || 0) > 0
                            || session.hasActivePairings) && (
                            <button
                              type="button"
                              className="next-btn next-btn--sm next-btn--danger"
                              disabled={busy}
                              onClick={() => deleteSession(session)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {editing && (
              <div className="next-form__section">
                <div className="next-card__head"><h2>Editing a session</h2></div>
                {sessionForm(editing, setEditing)}
                <div className="next-form__actions">
                  <button
                    type="button"
                    className="next-btn next-btn--primary"
                    disabled={!valid(editing) || busy}
                    onClick={saveSession}
                  >
                    Save session
                  </button>
                  <button type="button" className="next-btn" onClick={() => setEditing(null)}>Cancel</button>
                </div>
              </div>
            )}
          </section>
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                { label: 'All events', to: '/next/play-events' },
                { label: 'Play', hint: 'tools and the players export', to: '/next/play' },
                { label: 'Ambassador applications', to: '/next/ambassador-applications' }
              ]}
            />
          </section>
        </aside>
      </div>

      <Modal {...modalProps} />
    </div>
  );
};

PlayEventInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const PlayEvent = withApi(PlayEventInternal);

PlayEvent.requiresLogin = true;

export default PlayEvent;
